'use client';

/**
 * UgWarrantyExtensionFeature — FeatureRoot for the UG warranty extension page.
 *
 * Embeds a Salesforce iframe that handles the warranty extension flow.
 * The session token is fetched from our BFF to keep the API key server-side.
 */

import { Button } from '@/components/ui';
import { getLanguageFromLocale } from '@/lib/i18n/config';
import { useLocale, useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';

import styles from './UgWarrantyExtension.module.scss';

function originOf(url: string | null): string | null {
  if (!url) {
    return null;
  }
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

interface UgWarrantyExtensionFeatureProps {
  readonly widgetUrl: string | null;
}

export function UgWarrantyExtensionFeature({ widgetUrl }: UgWarrantyExtensionFeatureProps) {
  const locale = useLocale();
  const t = useTranslations('ug.warrantyExtension');

  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [iframeHeight, setIframeHeight] = useState<number | null>(null);

  const lang = getLanguageFromLocale(locale);

  const fetchSession = useCallback(async () => {
    try {
      const response = await fetch(`/api/ewarranty/session?lang=${encodeURIComponent(lang)}`);
      const data = await response.json();

      if (data.success && data.data?.token) {
        setToken(data.data.token);
        setError(false);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    }
  }, [lang]);

  useEffect(() => {
    if (!widgetUrl) {
      setError(true);
      return;
    }
    fetchSession();
  }, [fetchSession, widgetUrl]);

  useEffect(() => {
    const expectedOrigin = originOf(widgetUrl);

    function handleMessage(event: MessageEvent) {
      if (expectedOrigin && event.origin !== expectedOrigin) {
        return;
      }
      if (event.data?.type === 'ewarranty:height' && typeof event.data.height === 'number') {
        setIframeHeight(event.data.height);
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [widgetUrl]);

  const iframeSrc =
    token && widgetUrl ? `${widgetUrl}?t=${encodeURIComponent(token)}&lang=${lang}` : null;

  return (
    <article data-testid="UgWarrantyExtension">
      {/* Live renders the embed alone, so the heading is for assistive tech and
          the skip link only — without it `main` holds nothing but a foreign iframe. */}
      <h1 className="sr-only">{t('title')}</h1>

      <div className={styles.container}>
        {error && (
          <div className={styles.error} role="alert" data-testid="UgWarrantyExtensionError">
            <p>{t('errorMessage')}</p>
            <Button variant="primary" className={styles.retry} onClick={fetchSession}>
              {t('retry')}
            </Button>
          </div>
        )}

        {!error && !iframeSrc && (
          <output data-testid="UgWarrantyExtensionLoading">
            <span className="sr-only">{t('loading')}</span>
            <div className={styles.loading}>
              <span className={styles.dot} aria-hidden="true" />
              <span className={styles.dot} aria-hidden="true" />
              <span className={styles.dot} aria-hidden="true" />
            </div>
          </output>
        )}

        {iframeSrc && (
          /* Salesforce widget requires script execution + same-origin; sandbox with
             both flags escapes isolation, so we omit it (see CembraScript). */
          <iframe
            src={iframeSrc}
            title={t('iframeTitle')}
            className={styles.iframe}
            style={iframeHeight === null ? undefined : { height: `${iframeHeight}px` }}
            data-testid="UgWarrantyExtensionIframe"
          />
        )}
      </div>
    </article>
  );
}
